import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { authAPI } from '../services/api';
import { storeAuth } from '../services/auth';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
// This page handles the Google OAuth callback. Emergent redirects the user
// back here with `#session_id=<opaque>` in the URL hash. We POST that to the
// backend, receive our app's JWT + user, then route by role.
export default function GoogleCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false); // synchronous StrictMode guard
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError('No session code returned by Google. Please try again.');
      return;
    }

    (async () => {
      try {
        const { data } = await authAPI.googleCallback({ session_id: sessionId, keep_logged_in: true });
        const { token, user } = data;
        // Store using the same helper as email OTP path so downstream code
        // (PrivateRoute, PushNotificationManager, etc.) sees an identical state.
        storeAuth(token, user, true);
        toast.success(`Welcome back, ${user.name || 'Resident'}!`);
        // Role-based routing (matches App.js private route structure).
        const roleRouteMap = {
          resident: '/resident',
          guard: '/guard',
          management: '/management',
          super_admin: '/super-admin',
        };
        const target = roleRouteMap[user.role] || '/';
        // Strip the hash before navigating so a page refresh doesn't loop.
        window.history.replaceState(null, '', window.location.pathname);
        navigate(target, { replace: true });
      } catch (e) {
        const detail = e.response?.data?.detail || 'Google sign-in failed. Please try again.';
        setError(detail);
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gk-navy via-gk-navy-light to-gk-navy flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white/95 rounded-xl shadow-2xl p-8 text-center">
        {error ? (
          <>
            <div className="w-14 h-14 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center text-red-600 text-3xl font-bold">!</div>
            <h2 className="text-lg font-bold text-slate-800 mb-2" data-testid="google-callback-error-title">
              Sign-in couldn&apos;t complete
            </h2>
            <p className="text-sm text-slate-600 mb-6" data-testid="google-callback-error-message">
              {error}
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full h-11 bg-gk-navy-light hover:bg-gk-navy text-white rounded-lg font-medium"
              data-testid="google-callback-back-button"
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-gk-navy mx-auto mb-4" />
            <h2 className="text-lg font-bold text-slate-800 mb-1">Signing you in…</h2>
            <p className="text-sm text-slate-500">Verifying your Google account with GateKeeper Pro.</p>
          </>
        )}
      </div>
    </div>
  );
}
